import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../config/firebaseConfig';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, Pause, Volume2, VolumeX, Maximize, Minimize,
  FastForward, Rewind, X, Check, Award, Clock, 
  ChevronRight, ChevronLeft, BookOpen, FileText,
  AlertCircle, CheckCircle, XCircle, RotateCcw,
  Download, List, HelpCircle
} from 'react-feather';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { QUIZ_SOURCE, drawQuizQuestions, gradeQuiz, recordQuizAttempt } from '../../utils/questionPool';
import styles from './EducationPlayer.module.css';

const EducationPlayer = ({ show, onHide, education, onComplete }) => {
  const { employeeData, employeeId } = useUserProfile();
  const userGroup = employeeData?.role;
  // Video refs
  const videoRef = useRef(null);
  const progressBarRef = useRef(null);
  
  // Video state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [buffered, setBuffered] = useState(0);
  const [videoError, setVideoError] = useState(false);
  
  // Progress state
  const [watchedProgress, setWatchedProgress] = useState(0);
  const [maxWatchedTime, setMaxWatchedTime] = useState(0);
  const [lastPosition, setLastPosition] = useState(0);
  
  // Chapter state
  const [activeChapter, setActiveChapter] = useState(0);
  const [showChapters, setShowChapters] = useState(true);
  
  // Pop quiz state
  const [showPopQuiz, setShowPopQuiz] = useState(false);
  const [currentPopQuiz, setCurrentPopQuiz] = useState(null);
  const [completedPopQuizzes, setCompletedPopQuizzes] = useState(new Set());
  const [popQuizAnswer, setPopQuizAnswer] = useState(null);
  const [popQuizResult, setPopQuizResult] = useState(null);
  
  // Final quiz state
  const [showFinalQuiz, setShowFinalQuiz] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [finalQuizAnswers, setFinalQuizAnswers] = useState({});
  const [showFinalResults, setShowFinalResults] = useState(false);
  const [finalQuizScore, setFinalQuizScore] = useState(0);
  const [passed, setPassed] = useState(false);
  // Sınav soruları: eğitimin kendi final soruları + departman soru havuzundan
  // her denemede yeniden çekilen rastgele sorular (Kalite Yönetimi › Soru Havuzu)
  const [examQuestions, setExamQuestions] = useState([]);
  const [examPassScore, setExamPassScore] = useState(70);
  const examQuestionsRef = useRef([]);

  const prepareExam = useCallback(async () => {
    const ownQuestions = education?.finalQuiz || [];
    let poolQuestions = [];
    let poolPassScore = 70;
    try {
      const drawn = await drawQuizQuestions(userGroup);
      // Havuz kimlikleri eğitimin kendi soru kimlikleriyle çakışmasın
      poolQuestions = drawn.questions.map((question) => ({ ...question, id: `pool_${question.id}` }));
      poolPassScore = drawn.passScore;
    } catch (error) {
      // Havuz okunamazsa eğitimin kendi sorularıyla devam edilir
      console.error('Soru havuzu yüklenemedi:', error);
    }
    const combined = [...ownQuestions, ...poolQuestions];
    examQuestionsRef.current = combined;
    setExamQuestions(combined);
    setExamPassScore(education?.passScore || poolPassScore);
  }, [education, userGroup]);

  useEffect(() => {
    if (show && education) prepareExam();
  }, [show, education, prepareExam]);
  
  // Certificate state
  const [certificateEarned, setCertificateEarned] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(true);

  // Format time helper
  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Load user progress on mount
  useEffect(() => {
    const loadProgress = async () => {
      if (!auth.currentUser || !education) return;
      
      try {
        setIsLoading(true);
        const progressRef = doc(db, 'userEducationProgress', `${auth.currentUser.uid}_${education.id}`);
        const progressDoc = await getDoc(progressRef);
        
        if (progressDoc.exists()) {
          const data = progressDoc.data();
          setLastPosition(data.lastPosition || 0);
          setMaxWatchedTime(data.maxWatchedTime || 0);
          setWatchedProgress(data.watchedProgress || 0);
          setCompletedPopQuizzes(new Set(data.completedPopQuizzes || []));
          setCertificateEarned(data.certificateEarned || false);
          
          if (data.certificateEarned) {
            setPassed(true);
            setFinalQuizScore(data.finalQuizScore || 0);
          }
        }
      } catch (error) {
        console.error('Error loading progress:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (show && education) {
      loadProgress();
    }
  }, [show, education]);

  // Save progress to Firestore
  const saveProgress = useCallback(async (additionalData = {}) => {
    if (!auth.currentUser || !education) return;
    
    try {
      const progressRef = doc(db, 'userEducationProgress', `${auth.currentUser.uid}_${education.id}`);
      await setDoc(progressRef, {
        userId: auth.currentUser.uid,
        educationId: education.id,
        educationTitle: education.title,
        lastPosition: currentTime,
        maxWatchedTime: Math.max(maxWatchedTime, currentTime),
        watchedProgress: watchedProgress,
        completedPopQuizzes: Array.from(completedPopQuizzes),
        certificateEarned: certificateEarned,
        finalQuizScore: finalQuizScore,
        passed: passed,
        lastUpdated: serverTimestamp(),
        ...additionalData
      }, { merge: true });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  }, [currentTime, maxWatchedTime, watchedProgress, completedPopQuizzes, certificateEarned, finalQuizScore, passed, education]);

  // Refs to track high-frequency values without causing re-renders
  const maxWatchedTimeRef = useRef(maxWatchedTime);
  const lastUIUpdateRef = useRef(0);

  // Keep ref in sync when state changes from other sources
  useEffect(() => {
    maxWatchedTimeRef.current = maxWatchedTime;
  }, [maxWatchedTime]);

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const time = video.currentTime;
      const now = Date.now();

      // Update max watched time via ref (no re-render)
      if (time > maxWatchedTimeRef.current) {
        maxWatchedTimeRef.current = time;
      }

      // Check for pop quizzes on every tick (important - must not miss)
      if (education?.popQuizzes && !showPopQuiz) {
        const popQuiz = education.popQuizzes.find(quiz => {
          const quizTime = quiz.timestamp;
          return Math.abs(time - quizTime) < 0.5 && !completedPopQuizzes.has(quiz.id);
        });

        if (popQuiz) {
          video.pause();
          setIsPlaying(false);
          setCurrentPopQuiz(popQuiz);
          setShowPopQuiz(true);
          setPopQuizAnswer(null);
          setPopQuizResult(null);
          return;
        }
      }

      // Throttle UI state updates to ~4 times per second (every 250ms)
      if (now - lastUIUpdateRef.current < 250) return;
      lastUIUpdateRef.current = now;

      setCurrentTime(time);

      // Sync max watched time to state
      if (maxWatchedTimeRef.current > maxWatchedTime) {
        setMaxWatchedTime(maxWatchedTimeRef.current);
      }

      // Calculate progress percentage
      const progress = Math.floor((Math.max(maxWatchedTimeRef.current, time) / video.duration) * 100);
      setWatchedProgress(progress);

      // Update active chapter
      if (education?.chapters) {
        const chapterIndex = education.chapters.findIndex(
          ch => time >= ch.startTime && time < ch.endTime
        );
        if (chapterIndex !== -1) {
          setActiveChapter(chapterIndex);
        }
      }
    };
    
    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      
      // Resume from last position if available
      if (lastPosition > 0 && lastPosition < video.duration - 5) {
        video.currentTime = lastPosition;
        toast.success(`Kaldığınız yerden devam ediliyor: ${formatTime(lastPosition)}`);
      }
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
      setWatchedProgress(100);
      saveProgress({ watchedProgress: 100 });
      
      // Show final quiz if available and not already passed
      if (examQuestionsRef.current.length > 0 && !certificateEarned) {
        setTimeout(() => {
          setShowFinalQuiz(true);
        }, 1000);
      }
    };
    
    const handleProgress = () => {
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        setBuffered((bufferedEnd / video.duration) * 100);
      }
    };
    
    const handleError = () => {
      setVideoError(true);
    };
    
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('progress', handleProgress);
    video.addEventListener('error', handleError);
    
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('progress', handleProgress);
      video.removeEventListener('error', handleError);
    };
  }, [education, showPopQuiz, completedPopQuizzes, lastPosition, maxWatchedTime, certificateEarned, saveProgress]);

  // Auto-save progress every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (isPlaying) {
        saveProgress();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [isPlaying, saveProgress]);

  // Save progress on unmount
  useEffect(() => {
    return () => {
      if (currentTime > 0) {
        saveProgress();
      }
    };
  }, [currentTime, saveProgress]);

  // Video controls
  const togglePlay = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    if (!progressBarRef.current || !videoRef.current) return;
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const position = (e.clientX - rect.left) / rect.width;
    const newTime = position * duration;
    
    // Only allow seeking to already watched content (prevent skipping)
    if (newTime <= maxWatchedTime || newTime <= currentTime) {
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    } else {
      toast.error('Henüz izlemediğiniz bölümlere atlayamazsınız');
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    
    if (isMuted) {
      videoRef.current.volume = volume || 0.5;
      setIsMuted(false);
    } else {
      videoRef.current.volume = 0;
      setIsMuted(true);
    }
  };

  const skipForward = () => {
    if (!videoRef.current) return;
    const newTime = Math.min(currentTime + 10, maxWatchedTime);
    videoRef.current.currentTime = newTime;
  };

  const skipBackward = () => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.max(currentTime - 10, 0);
  };

  const seekToChapter = (chapter) => {
    if (!videoRef.current) return;
    
    if (chapter.startTime <= maxWatchedTime) {
      videoRef.current.currentTime = chapter.startTime;
      setCurrentTime(chapter.startTime);
    } else {
      toast.error('Bu bölümü izleyebilmek için önceki bölümleri tamamlamalısınız');
    }
  };

  const toggleFullscreen = () => {
    const container = document.querySelector(`.${styles.playerContainer}`);
    if (!container) return;
    
    if (!document.fullscreenElement) {
      container.requestFullscreen().then(() => setIsFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };

  // Pop quiz handlers
  const handlePopQuizAnswer = (answerIndex) => {
    setPopQuizAnswer(answerIndex);
  };

  const submitPopQuizAnswer = () => {
    if (popQuizAnswer === null || !currentPopQuiz) return;
    
    const isCorrect = popQuizAnswer === currentPopQuiz.correctAnswer;
    setPopQuizResult(isCorrect);
    
    if (isCorrect) {
      setCompletedPopQuizzes(prev => new Set([...prev, currentPopQuiz.id]));
    }
  };

  const continueAfterPopQuiz = () => {
    if (!popQuizResult && currentPopQuiz) {
      // Wrong answer - restart from 10 seconds before the quiz
      const restartTime = Math.max(0, currentPopQuiz.timestamp - 10);
      if (videoRef.current) {
        videoRef.current.currentTime = restartTime;
      }
      toast.error('Yanlış cevap! Lütfen bu bölümü tekrar izleyin.');
    }
    
    setShowPopQuiz(false);
    setCurrentPopQuiz(null);
    setPopQuizAnswer(null);
    setPopQuizResult(null);
    
    // Resume video
    if (videoRef.current && popQuizResult) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  // Final quiz handlers
  const handleFinalQuizAnswer = (questionId, answerIndex) => {
    setFinalQuizAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };

  const submitFinalQuiz = async () => {
    if (examQuestions.length === 0) return;

    const { score, correctCount, passed: hasPassed } = gradeQuiz(examQuestions, finalQuizAnswers, examPassScore);
    recordQuizAttempt({
      userId: auth.currentUser?.uid || '',
      employeeId: employeeId || '',
      employeeName: `${employeeData?.eName || ''} ${employeeData?.eSurname || ''}`.trim(),
      group: employeeData?.role || '',
      sourceType: QUIZ_SOURCE.EDUCATION,
      sourceId: education?.id || '',
      sourceName: education?.title || '',
      questionIds: examQuestions.map((question) => question.id),
      questionCount: examQuestions.length,
      correctCount,
      score,
      passScore: examPassScore,
      passed: hasPassed,
    });
    
    setFinalQuizScore(score);
    setPassed(hasPassed);
    setShowFinalResults(true);
    
    if (hasPassed) {
      setCertificateEarned(true);
      await saveProgress({
        certificateEarned: true,
        finalQuizScore: score,
        passed: true,
        completedAt: serverTimestamp()
      });
      toast.success('Tebrikler! Sertifikanızı kazandınız!');
    }
  };

  const retakeFinalQuiz = () => {
    // Her denemede havuzdan yeni sorular çekilir
    prepareExam();
    setFinalQuizAnswers({});
    setCurrentQuestionIndex(0);
    setShowFinalResults(false);
  };

  const handleClose = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    saveProgress();
    onHide();
  };

  const handleCompleteEducation = () => {
    if (onComplete) {
      onComplete(education.id);
    }
    handleClose();
  };

  if (!show) return null;

  // Check if education deadline has passed
  const isExpired = education?.hasDeadline && education?.deadline && new Date(education.deadline) < new Date();

  if (isExpired) {
    return (
      <div className={styles.playerOverlay}>
        <motion.div
          className={styles.playerContainer}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{ maxWidth: '500px', textAlign: 'center', padding: '3rem 2rem' }}
        >
          <div style={{ marginBottom: '1.5rem' }}>
            <AlertCircle size={64} color="#dc2626" />
          </div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.75rem', color: '#1a202c' }}>
            Süre Doldu
          </h2>
          <p style={{ color: '#718096', marginBottom: '0.5rem', fontSize: '1rem' }}>
            Bu eğitimin son katılma tarihi geçmiştir.
          </p>
          <p style={{ color: '#a0aec0', marginBottom: '2rem', fontSize: '0.9rem' }}>
            Son Katılma Tarihi: {new Date(education.deadline).toLocaleDateString('tr-TR')}
          </p>
          <button
            onClick={onHide}
            style={{
              padding: '0.75rem 2rem',
              borderRadius: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              fontWeight: 600,
              fontSize: '1rem',
              cursor: 'pointer'
            }}
          >
            Kapat
          </button>
        </motion.div>
      </div>
    );
  }

  // Render pop quiz modal
  const renderPopQuiz = () => (
    <motion.div 
      className={styles.popQuizOverlay}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div 
        className={styles.popQuizModal}
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
      >
        <div className={styles.popQuizHeader}>
          <HelpCircle size={28} />
          <h3>Pop Quiz!</h3>
          <p>Bu soruyu doğru cevaplamalısınız</p>
        </div>
        
        <div className={styles.popQuizBody}>
          <div className={styles.popQuizQuestion}>
            {currentPopQuiz?.question}
          </div>
          
          <div className={styles.popQuizOptions}>
            {currentPopQuiz?.options.map((option, index) => (
              <button
                key={index}
                className={`${styles.popQuizOption} ${popQuizAnswer === index ? styles.selected : ''} ${
                  popQuizResult !== null ? (
                    index === currentPopQuiz.correctAnswer ? styles.correct : 
                    popQuizAnswer === index && !popQuizResult ? styles.wrong : ''
                  ) : ''
                }`}
                onClick={() => !popQuizResult && handlePopQuizAnswer(index)}
                disabled={popQuizResult !== null}
              >
                <span className={styles.optionLetter}>{String.fromCharCode(65 + index)}</span>
                <span className={styles.optionText}>{option}</span>
                {popQuizResult !== null && index === currentPopQuiz.correctAnswer && (
                  <CheckCircle className={styles.optionIcon} />
                )}
                {popQuizResult === false && popQuizAnswer === index && (
                  <XCircle className={styles.optionIcon} />
                )}
              </button>
            ))}
          </div>
          
          {popQuizResult !== null && currentPopQuiz?.explanation && (
            <div className={`${styles.popQuizExplanation} ${popQuizResult ? styles.correct : styles.wrong}`}>
              <AlertCircle size={18} />
              <span>{currentPopQuiz.explanation}</span>
            </div>
          )}
        </div>
        
        <div className={styles.popQuizFooter}>
          {popQuizResult === null ? (
            <button 
              className={styles.submitQuizBtn}
              onClick={submitPopQuizAnswer}
              disabled={popQuizAnswer === null}
            >
              Cevabı Gönder
            </button>
          ) : (
            <button 
              className={`${styles.continueBtn} ${popQuizResult ? styles.success : styles.retry}`}
              onClick={continueAfterPopQuiz}
            >
              {popQuizResult ? (
                <>Devam Et <ChevronRight size={18} /></>
              ) : (
                <><RotateCcw size={18} /> Tekrar İzle</>
              )}
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );

  // Render final quiz modal
  const renderFinalQuiz = () => {
    const questions = examQuestions;
    const currentQuestion = questions[currentQuestionIndex];
    
    return (
      <motion.div 
        className={styles.finalQuizOverlay}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div 
          className={styles.finalQuizModal}
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
        >
          {!showFinalResults ? (
            <>
              <div className={styles.finalQuizHeader}>
                <div className={styles.quizHeaderLeft}>
                  <Award size={28} />
                  <div>
                    <h3>Final Quiz</h3>
                    <p>Geçme puanı: {examPassScore}%</p>
                  </div>
                </div>
                <div className={styles.quizProgress}>
                  {currentQuestionIndex + 1} / {questions.length}
                </div>
              </div>
              
              <div className={styles.questionProgressBar}>
                <div 
                  className={styles.questionProgressFill}
                  style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                />
              </div>
              
              <div className={styles.finalQuizBody}>
                <div className={styles.questionNumber}>Soru {currentQuestionIndex + 1}</div>
                <div className={styles.questionText}>{currentQuestion?.question}</div>
                
                <div className={styles.answerOptions}>
                  {currentQuestion?.options.map((option, index) => (
                    <button
                      key={index}
                      className={`${styles.answerOption} ${finalQuizAnswers[currentQuestion.id] === index ? styles.selected : ''}`}
                      onClick={() => handleFinalQuizAnswer(currentQuestion.id, index)}
                    >
                      <span className={styles.answerLetter}>{String.fromCharCode(65 + index)}</span>
                      <span className={styles.answerText}>{option}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className={styles.finalQuizFooter}>
                <button
                  className={styles.quizNavBtn}
                  onClick={() => setCurrentQuestionIndex(prev => prev - 1)}
                  disabled={currentQuestionIndex === 0}
                >
                  <ChevronLeft size={18} /> Önceki
                </button>
                
                {currentQuestionIndex < questions.length - 1 ? (
                  <button
                    className={styles.quizNavBtn}
                    onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                    disabled={finalQuizAnswers[currentQuestion?.id] === undefined}
                  >
                    Sonraki <ChevronRight size={18} />
                  </button>
                ) : (
                  <button
                    className={styles.submitFinalBtn}
                    onClick={submitFinalQuiz}
                    disabled={Object.keys(finalQuizAnswers).length < questions.length}
                  >
                    Sınavı Bitir
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className={styles.quizResults}>
              <div className={`${styles.resultIcon} ${passed ? styles.success : styles.fail}`}>
                {passed ? <CheckCircle size={64} /> : <XCircle size={64} />}
              </div>
              
              <h3>{passed ? 'Tebrikler!' : 'Maalesef'}</h3>
              
              <div className={styles.scoreDisplay}>
                <span className={styles.scoreValue}>{finalQuizScore}%</span>
                <span className={styles.scoreLabel}>Başarı Puanı</span>
              </div>
              
              <p className={styles.resultMessage}>
                {passed 
                  ? 'Sınavı başarıyla geçtiniz ve sertifikanızı kazandınız!'
                  : `Geçmek için en az ${examPassScore}% puan almanız gerekiyor.`
                }
              </p>
              
              <div className={styles.resultActions}>
                {passed ? (
                  <>
                    <button 
                      className={styles.viewCertificateBtn}
                      onClick={() => {
                        setShowFinalQuiz(false);
                        setShowCertificate(true);
                      }}
                    >
                      <Award size={18} /> Sertifikayı Görüntüle
                    </button>
                    <button 
                      className={styles.completeBtn}
                      onClick={handleCompleteEducation}
                    >
                      <Check size={18} /> Eğitimi Tamamla
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      className={styles.retakeBtn}
                      onClick={retakeFinalQuiz}
                    >
                      <RotateCcw size={18} /> Tekrar Dene
                    </button>
                    <button 
                      className={styles.reviewBtn}
                      onClick={() => setShowFinalQuiz(false)}
                    >
                      <BookOpen size={18} /> Videoyu Tekrar İzle
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    );
  };

  // Render certificate
  const renderCertificate = () => (
    <motion.div 
      className={styles.certificateOverlay}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div 
        className={styles.certificateModal}
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
      >
        <button className={styles.closeCertificateBtn} onClick={() => setShowCertificate(false)}>
          <X size={24} />
        </button>
        
        <div className={styles.certificate}>
          <div className={styles.certificateHeader}>
            <Award size={48} className={styles.certificateIcon} />
            <h2>Başarı Sertifikası</h2>
          </div>
          
          <div className={styles.certificateBody}>
            <p className={styles.certificateIntro}>Bu sertifika</p>
            <p className={styles.certificateName}>{auth.currentUser?.displayName || 'Katılımcı'}</p>
            <p className={styles.certificateText}>adlı katılımcının</p>
            <p className={styles.certificateCourse}>"{education?.title}"</p>
            <p className={styles.certificateText}>
              eğitimini <strong>{finalQuizScore}%</strong> başarı puanıyla tamamladığını onaylar.
            </p>
          </div>
          
          <div className={styles.certificateFooter}>
            <div className={styles.certificateDate}>
              Tarih: {new Date().toLocaleDateString('tr-TR')}
            </div>
            <div className={styles.certificateSignature}>
              <div className={styles.signatureLine}></div>
              <span>HabcareCRO Kalite Yönetimi</span>
            </div>
          </div>
        </div>
        
        <button className={styles.downloadCertificateBtn}>
          <Download size={18} /> Sertifikayı İndir
        </button>
      </motion.div>
    </motion.div>
  );

  return (
    <div className={styles.modalOverlay}>
      <AnimatePresence>
        <motion.div 
          className={styles.modal}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
        >
          {/* Header */}
          <div className={styles.modalHeader}>
            <div className={styles.headerInfo}>
              <h2>{education?.title}</h2>
              <div className={styles.headerMeta}>
                {education?.instructor && <span><BookOpen size={14} /> {education.instructor}</span>}
                <span><Clock size={14} /> {formatTime(duration)}</span>
                <span className={styles.progressBadge}>{watchedProgress}% tamamlandı</span>
              </div>
            </div>
            <button className={styles.closeBtn} onClick={handleClose}>
              <X size={24} />
            </button>
          </div>

          {/* Main Content */}
          <div className={styles.mainContent}>
            {/* Video Player */}
            <div className={styles.playerContainer}>
              <div className={styles.videoWrapper}>
                {videoError ? (
                  <div className={styles.videoError}>
                    <AlertCircle size={48} />
                    <p>Video yüklenemedi</p>
                  </div>
                ) : (
                  <>
                    <video
                      ref={videoRef}
                      src={education?.videoUrl}
                      className={styles.video}
                      onClick={togglePlay}
                    />
                    
                    {!isPlaying && (
                      <div className={styles.playOverlay} onClick={togglePlay}>
                        <motion.div 
                          className={styles.playButton}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Play size={40} />
                        </motion.div>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Video Controls */}
              <div className={styles.videoControls}>
                {/* Progress Bar */}
                <div 
                  className={styles.progressBar}
                  ref={progressBarRef}
                  onClick={handleSeek}
                >
                  <div 
                    className={styles.bufferedBar}
                    style={{ width: `${buffered}%` }}
                  />
                  <div 
                    className={styles.watchedBar}
                    style={{ width: `${(maxWatchedTime / duration) * 100}%` }}
                  />
                  <div 
                    className={styles.progressFill}
                    style={{ width: `${(currentTime / duration) * 100}%` }}
                  />
                  <div 
                    className={styles.progressHandle}
                    style={{ left: `${(currentTime / duration) * 100}%` }}
                  />
                  
                  {/* Pop quiz markers */}
                  {education?.popQuizzes?.map(quiz => (
                    <div
                      key={quiz.id}
                      className={`${styles.quizMarker} ${completedPopQuizzes.has(quiz.id) ? styles.completed : ''}`}
                      style={{ left: `${(quiz.timestamp / duration) * 100}%` }}
                      title={`Pop Quiz - ${formatTime(quiz.timestamp)}`}
                    />
                  ))}
                  
                  {/* Chapter markers */}
                  {education?.chapters?.map((chapter, index) => (
                    <div
                      key={chapter.id}
                      className={styles.chapterMarker}
                      style={{ left: `${(chapter.startTime / duration) * 100}%` }}
                      title={chapter.title}
                    />
                  ))}
                </div>
                
                {/* Control Buttons */}
                <div className={styles.controlsRow}>
                  <div className={styles.controlsLeft}>
                    <button className={styles.controlBtn} onClick={togglePlay}>
                      {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                    </button>
                    <button className={styles.controlBtn} onClick={skipBackward}>
                      <Rewind size={18} />
                    </button>
                    <button className={styles.controlBtn} onClick={skipForward}>
                      <FastForward size={18} />
                    </button>
                    
                    <div className={styles.volumeControl}>
                      <button className={styles.controlBtn} onClick={toggleMute}>
                        {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                      </button>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={isMuted ? 0 : volume}
                        onChange={handleVolumeChange}
                        className={styles.volumeSlider}
                      />
                    </div>
                    
                    <span className={styles.timeDisplay}>
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </span>
                  </div>
                  
                  <div className={styles.controlsRight}>
                    <button 
                      className={styles.controlBtn}
                      onClick={() => setShowChapters(!showChapters)}
                    >
                      <List size={18} />
                    </button>
                    <button className={styles.controlBtn} onClick={toggleFullscreen}>
                      {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Sidebar - Chapters */}
            {showChapters && (
              <div className={styles.sidebar}>
                <div className={styles.sidebarHeader}>
                  <h3><List size={18} /> İçerik</h3>
                  <div className={styles.progressRing}>
                    <svg viewBox="0 0 36 36">
                      <path
                        className={styles.progressRingBg}
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <path
                        className={styles.progressRingFill}
                        strokeDasharray={`${watchedProgress}, 100`}
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                    </svg>
                    <span>{watchedProgress}%</span>
                  </div>
                </div>
                
                <div className={styles.chaptersList}>
                  {education?.chapters?.map((chapter, index) => (
                    <button
                      key={chapter.id}
                      className={`${styles.chapterItem} ${activeChapter === index ? styles.active : ''} ${
                        chapter.startTime <= maxWatchedTime ? styles.unlocked : styles.locked
                      }`}
                      onClick={() => seekToChapter(chapter)}
                    >
                      <div className={styles.chapterNumber}>{index + 1}</div>
                      <div className={styles.chapterInfo}>
                        <span className={styles.chapterTitle}>{chapter.title}</span>
                        <span className={styles.chapterDuration}>
                          {formatTime(chapter.startTime)} - {formatTime(chapter.endTime)}
                        </span>
                      </div>
                      {chapter.startTime <= maxWatchedTime && (
                        <CheckCircle size={16} className={styles.chapterCheck} />
                      )}
                    </button>
                  ))}
                </div>
                
                {/* Quiz Status */}
                {examQuestions.length > 0 && (
                  <div className={styles.quizStatus}>
                    <div className={styles.quizStatusHeader}>
                      <Award size={18} />
                      <span>Final Quiz</span>
                    </div>
                    {certificateEarned ? (
                      <div className={styles.quizPassed}>
                        <CheckCircle size={16} />
                        <span>Tamamlandı - {finalQuizScore}%</span>
                      </div>
                    ) : watchedProgress >= 95 ? (
                      <button 
                        className={styles.takeQuizBtn}
                        onClick={() => setShowFinalQuiz(true)}
                      >
                        Sınava Başla
                      </button>
                    ) : (
                      <p className={styles.quizLocked}>
                        Videoyu tamamlayın (%95)
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Pop Quiz Modal */}
          <AnimatePresence>
            {showPopQuiz && renderPopQuiz()}
          </AnimatePresence>
          
          {/* Final Quiz Modal */}
          <AnimatePresence>
            {showFinalQuiz && renderFinalQuiz()}
          </AnimatePresence>
          
          {/* Certificate Modal */}
          <AnimatePresence>
            {showCertificate && renderCertificate()}
          </AnimatePresence>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default EducationPlayer;
